import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n";
import { SERVICES } from "@/constants/testIds";
import { SERVICES_LIST } from "@/data/services";

export default function Services() {
  const ref = useRef(null);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);

  return (
    <div
      ref={ref}
      data-testid={SERVICES.root}
      className="relative w-full h-screen overflow-hidden grain"
      style={{ background: "var(--ink)" }}
    >
      {/* atmospheric orb */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: 880, height: 880, top: "-25%", left: "-15%",
          background: "radial-gradient(circle, var(--violet) 0%, transparent 62%)",
          opacity: 0.13, filter: "blur(40px)",
        }}
      />
      {/* vertical rule grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
          backgroundSize: "12.5% 100%",
        }}
      />

      <div className="relative z-10 h-full px-5 sm:px-8 md:px-16 lg:px-24 pt-32 sm:pt-36 md:pt-32 pb-8 sm:pb-10 flex flex-col text-white">
        {/* Top eyebrow row */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4 sm:pb-5 mb-6 sm:mb-8">
          <div className="flex items-baseline gap-4 sm:gap-6">
            <span className="font-mono text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.32em] text-white/40">
              {lang === "id" ? "Layanan" : "Services"} · 04
            </span>
            <span className="font-mono text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.32em] text-white/40 hidden md:inline">
              {lang === "id" ? "Empat disiplin · konsultasi tunggal" : "Four disciplines · one consultancy"}
            </span>
          </div>
          <span className="font-mono text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.28em] text-white/40">
            /04 · /09
          </span>
        </div>

        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 mb-6 sm:mb-8 lg:mb-10">
          <div className="lg:col-span-7">
            <h2
              className="font-display text-white"
              style={{
                fontSize: "clamp(1.5rem, 3.6vw, 3rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
                fontWeight: 600,
              }}
            >
              {t("services.intro")}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-2">
            <p className="text-white/55 text-[0.875rem] sm:text-sm leading-relaxed border-l border-white/15 pl-4 sm:pl-5 max-w-[26rem]">
              {lang === "id"
                ? "Setiap disiplin berdiri sendiri dan saling melengkapi. Pilih satu titik masuk; kami akan menyusun sisa peta."
                : "Each discipline stands alone and reinforces the others. Pick one entry point; we'll compose the rest of the map."}
            </p>
          </div>
        </div>

        {/* Catalogue rows */}
        <ol className="flex-1 min-h-0 border-t border-white/10 flex flex-col overflow-hidden">
          {SERVICES_LIST.map((s, i) => {
            const isHover = hovered === i;
            return (
              <li
                key={s.slug}
                className="flex-1 min-h-0 border-b border-white/10"
              >
                <button
                  type="button"
                  data-testid={SERVICES.item(i + 1)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(i)}
                  onBlur={() => setHovered(null)}
                  onClick={() => navigate(`/services/${s.slug}`)}
                  className="group relative w-full h-full flex items-center justify-between gap-4 sm:gap-6 md:gap-10 px-2 sm:px-3 md:px-4 transition-all duration-500 text-left"
                  style={{
                    background: isHover ? `linear-gradient(90deg, ${s.accent}14, transparent 60%)` : "transparent",
                  }}
                >
                  {/* accent bar */}
                  <span
                    aria-hidden
                    className="absolute left-0 top-0 h-full transition-all duration-500"
                    style={{
                      width: isHover ? 4 : 0,
                      background: s.accent,
                    }}
                  />

                  <div className="flex items-baseline gap-4 sm:gap-6 md:gap-8 min-w-0 flex-1">
                    <span
                      className="font-mono text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.3em] transition-colors duration-500 flex-none"
                      style={{ color: isHover ? s.accent : "rgba(255,255,255,0.35)" }}
                    >
                      /0{i + 1}
                    </span>
                    <h3
                      className="font-display leading-none transition-all duration-500 truncate min-w-0 flex-1"
                      style={{
                        fontSize: "clamp(1rem, 2.8vw, 2.4rem)",
                        letterSpacing: "-0.02em",
                        fontWeight: 600,
                        color: isHover ? "#ffffff" : "rgba(255,255,255,0.78)",
                        transform: isHover ? "translateX(10px)" : "translateX(0)",
                      }}
                    >
                      {s.name}
                    </h3>
                  </div>

                  {/* Tagline reveal */}
                  <div
                    className="hidden lg:flex flex-1 items-center justify-end gap-6 transition-all duration-500"
                    style={{ opacity: isHover ? 1 : 0, transform: isHover ? "translateX(0)" : "translateX(20px)" }}
                  >
                    <span className="font-display italic text-white/75 text-sm md:text-[15px] text-right max-w-[24rem] truncate" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                      {s.tagline[lang]}
                    </span>
                    <span
                      className="font-mono text-[0.6rem] uppercase tracking-[0.28em]"
                      style={{ color: s.accent }}
                    >
                      {lang === "id" ? "Buka" : "Open"}
                    </span>
                  </div>

                  {/* arrow */}
                  <ArrowUpRight
                    size={16}
                    className="transition-all duration-500 flex-none sm:w-[18px] sm:h-[18px]"
                    style={{
                      color: isHover ? s.accent : "rgba(255,255,255,0.30)",
                      transform: isHover ? "translate(0,0) rotate(0deg)" : "translate(-4px,4px) rotate(-25deg)",
                    }}
                  />
                </button>
              </li>
            );
          })}
        </ol>

        {/* Bottom rule */}
        <div className="mt-4 sm:mt-5 flex items-center justify-between font-mono text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.28em] text-white/40">
          <span>{lang === "id" ? "Disiplin · Konsultasi" : "Disciplines · Consulting"}</span>
          <span>MK · CATALOGUE</span>
        </div>
      </div>
    </div>
  );
}
